import { PureComponent, ReactNode } from 'react';
import FullScreenLoader from '../../components/FullScreenLoader';
import SignaturePadDialog from './SignaturePadDialog';
import { RouteComponentProps } from 'react-router-dom';
import { ConsentFormPageRouterProps } from '../routes';
import fetchConsentForm, { ConsentForm } from '../../api/fetchConsentForm';
import { track } from '../../tracking/mixpanel';
import uploadPtSign from '../../api/uploadPtSign';
import addSignToConsentForm from '../../api/addSignToConsentForm';
import AppLoader from '../../components/AppLoader';

type Props = RouteComponentProps<ConsentFormPageRouterProps>;

type State = {
    isSignPadJsLoaded?: boolean;
    isSignDialogVisible?: boolean;
    signData?: string;
    isFetching?: boolean;
    consentForm?: ConsentForm;
    pushState?: '' | 'pushing' | 'success' | 'error';
};

class ConsentFormPage extends PureComponent<Props, State> {
    state: State = {
        isSignPadJsLoaded: false,
        isFetching: true,
    };

    onSign = async (): Promise<void> => {
        const { consentForm, signData, pushState } = this.state;
        const { id } = this.props.match.params;

        if (pushState === 'success' || consentForm?.patientSignUrl?.length) {
            window.open(consentForm?.url);
            return;
        }

        if (!id) {
            return;
        }

        if (signData && consentForm) {
            this.setState({ pushState: 'pushing' });

            const formData = new FormData();
            const currentTimestamp = new Date().getTime();

            const base64Response = await fetch(signData);
            const blob = await base64Response.blob();

            const file = new File([blob], 'header-footer', {
                type: 'image/png',
            });

            formData.append(
                'header-footer',
                file,
                `${consentForm?.patientid}-${id}-${currentTimestamp}`,
            );

            try {
                const { success, url: signUrl, error = '' } = await uploadPtSign(formData);

                if (success) {
                    const { url: finalPdfUrl } = await addSignToConsentForm(id, signUrl);

                    this.setState({
                        pushState: 'success',
                        consentForm: {
                            ...consentForm,
                            url: finalPdfUrl + '?t=' + new Date().getTime(),
                        },
                    });
                } else {
                    track('ERROR', {
                        on: 'consent-form',
                        error: `Error uploading sign: ${error}`,
                    });

                    this.setState({ pushState: 'error' });
                }
            } catch (e) {
                this.setState({ pushState: 'error' });
            }
        } else {
            this.setState({ isSignDialogVisible: true });
        }
    };

    componentDidMount(): void {
        // load a srcipt
        const script = document.createElement('script');
        script.src =
            'https://cdn.jsdelivr.net/npm/signature_pad@4.0.0/dist/signature_pad.umd.min.js';
        script.async = true;
        script.onload = () => {
            this.setState({ isSignPadJsLoaded: true });
        };
        document.body.appendChild(script);

        if (!this.props.match.params.id) {
            track('ERROR', { on: 'consent-form', error: 'id not found' });
            return;
        }

        fetchConsentForm(this.props.match.params.id)
            .then((res) => {
                if (!res.data) {
                    track('ERROR', { on: 'consent-form', error: 'API failed' });
                    alert('Error fetching consent form');
                }
                this.setState({ consentForm: res.data, isFetching: false });
            })
            .catch((err) => {
                track('ERROR', { on: 'consent-form', error: err });
                alert('Error fetching consent form');
            });
    }

    closeSignaturePad = (sign?: string): void => {
        this.setState({ isSignDialogVisible: false, signData: sign });
    };

    onClear = (): void => {
        this.setState({ signData: undefined });
    };

    render(): ReactNode {
        const {
            isSignPadJsLoaded,
            isSignDialogVisible,
            signData,
            isFetching,
            consentForm,
            pushState,
        } = this.state;

        if (!isSignPadJsLoaded || isFetching) {
            return (
                <div className="w-full h-full">
                    <FullScreenLoader />
                </div>
            );
        }

        return (
            <div className="w-full h-full flex overflow-hidden flex flex-col">
                {isSignDialogVisible ? (
                    <SignaturePadDialog closeMe={this.closeSignaturePad} />
                ) : null}
                {consentForm?.url && (
                    <div className="flex flex-1 flex-col overflow-auto">
                        <iframe
                            className="h-full"
                            src={`/pdfjs-1/web/viewer.html?file=${consentForm?.url}#zoom=65`}
                        />
                    </div>
                )}
                <div>
                    {signData && pushState !== 'success' && pushState !== 'pushing' && (
                        <div className="w-full flex items-center justify-center">
                            <img style={{ height: 48 }} src={signData} />
                        </div>
                    )}
                    <div
                        id="footer"
                        className="flex items-center bg-white justify-between w-full px-16 py-12 shadow-usecase-bottom-sheet"
                    >
                        <div>
                            {signData &&
                                pushState !== 'success' &&
                                pushState !== 'pushing' &&
                                !consentForm?.patientSignUrl && (
                                    <button
                                        onClick={this.onClear}
                                        className="px-16 py-10 secondary-btn btn"
                                    >
                                        Clear
                                    </button>
                                )}
                        </div>

                        <button
                            onClick={this.onSign}
                            className="px-16 py-10 primary-btn btn"
                            disabled={pushState === 'pushing'}
                        >
                            {pushState === 'success' || consentForm?.patientSignUrl?.length ? (
                                <span>Download</span>
                            ) : (
                                <span>{signData ? 'Submit' : 'Sign'}</span>
                            )}
                            {pushState === 'pushing' && <AppLoader />}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default ConsentFormPage;
